import './StyleDetails.scss';

import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FontLicenseType } from 'client/graphql/types/operations';

import formatCurrency from 'utils/formatCurrency';
import useCart from './utils/useCart';

interface Props {
  licenseOption: {
    id: number;
    type: FontLicenseType;
    multiplier: number;
  } | null;
  family: {
    id: number;
    name: string;
    completePrice: number;
    fontFacePrice: number;
    subPackageFontFacePrice: number;
    discountedFontFacePrice: number;
    faces: {
      id: number;
      width: { slug: string };
      size: { slug: string };
      weight: { slug: string };
      style: { slug: string };
    }[];
  };
  face: {
    id: number;
    width: { slug: string; name: string; position: number };
    size: { slug: string; name: string; position: number };
    weight: { slug: string; name: string; position: number };
    style: { slug: string; name: string; position: number };
    description: string;
    familyPreview: { url: string | null };
    family: { fontFacePrice: number; discountedFontFacePrice: number };
  };
  disabled: boolean;
}

const StyleDetails: FC<Props> = (props) => {
  const { family, face, disabled, licenseOption } = props;

  const { t } = useTranslation();
  const { totalPrice } = useCart({ licenseOption, family, faces: [face] });

  const hasComplementaryFace = useMemo(() => {
    if (face.style.slug === 'italic') {
      return family.faces.some(
        (familyFace) =>
          familyFace.size.slug === face.size.slug &&
          familyFace.width.slug === face.width.slug &&
          familyFace.weight.slug === face.weight.slug &&
          (familyFace.style.slug === 'upright' ||
            familyFace.style.slug === 'roman'),
      );
    }

    if (face.style.slug === 'upright' || face.style.slug === 'roman') {
      return family.faces.some(
        (familyFace) =>
          familyFace.size.slug === face.size.slug &&
          familyFace.width.slug === face.width.slug &&
          familyFace.weight.slug === face.weight.slug &&
          familyFace.style.slug === 'italic',
      );
    }

    return false;
  }, [face, family.faces]);

  return (
    <>
      {face.familyPreview.url != null ? (
        <img
          className="StyleDetails-styleName"
          src={face.familyPreview.url}
          alt={`${family.name} ${face.description}`}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div className="StyleDetails-styleNamePlaceholder">{`${family.name} ${face.description}`}</div>
      )}

      {disabled || (
        <div className="StyleDetails-priceLine">
          <div className="StyleDetails-priceLine-price">
            {formatCurrency(totalPrice)}
          </div>

          {hasComplementaryFace && (
            <div className="StyleDetails-priceLine-savings">
              {t('family.priceSavings', {
                amount: formatCurrency(
                  (family.fontFacePrice - family.discountedFontFacePrice) *
                    2 *
                    (licenseOption?.multiplier ?? 0),
                ),
                style:
                  face.style.slug === 'italic'
                    ? t('fontStyle.roman')
                    : t('fontStyle.italic'),
              })}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default StyleDetails;
